import { useMemo } from 'react';
import { useClientId } from '../../../../../../../../_hooks/useClientId';
import { useFpId } from '../../../../../../../hooks/useFpId';
import {
  MonteCarloPosRange,
  useLongevityAnalysisQuery,
} from '../../../../../../api/useLongevityAnalysisQuery';
import { palette } from '../../../../../../../../theme/palette';
import { SERIES_COLORS } from '../../../../../../../../CommonComponents/OPL/charts/charts.constants';
import { formatPercentage } from '../../../../../../../../CommonUtils/formatNumber';
import { renderDot } from './utils/renderDot';
import { renderTooltip } from './utils/renderTooltip';
import { renderVerticalLineWithSquare } from './utils/renderVerticalLineWithDot';

export const colorsMap = {
  green: palette.green['400'],
  yellow: palette.yellow['300'],
  red: palette.red['400'],
};

export const useChart = () => {
  const clientId = useClientId();

  const fpId = useFpId();

  const queryInfo = useLongevityAnalysisQuery({
    clientId,
    fpId,
  });

  const data = useMemo(() => {
    if (!queryInfo.data) {
      return null;
    }

    const {
      PrincipalName: principalName,
      PrincipalRetirementYear: principalRetirementYear,
      SpouseName: spouseName,
      SpouseRetirementYear: spouseRetirementYear,
      ChartData,
      PrincipalGender: principalGender,
      SpouseGender: spouseGender,
      MonteCarloHighRange: monteCarloHighRange,
      MonteCarloMediumRange: monteCarloMediumRange,
      MonteCarloLowRange: monteCarloLowRange,
      PrincipalLifeExpectancyYear: principalLifeExpectancy,
      SpouseLifeExpectancyYear: spouseLifeExpectancy,
    } = queryInfo.data;

    const categories = ChartData.map(item => item.CalendarYear);

    const probabilityOfSuccessChartSeries = ChartData.map(item => {
      return {
        y: item.MonteCarloProbabilityOfSuccess,
        principalAge: item.PrincipalAge,
        spouseAge: item.SpouseAge,
        color:
          item.MonteCarloPosRange === MonteCarloPosRange.HIGH
            ? colorsMap.green
            : item.MonteCarloPosRange === MonteCarloPosRange.MEDIUM
            ? colorsMap.yellow
            : colorsMap.red,
      };
    });

    const series = [
      { name: 'Probability of Success', data: probabilityOfSuccessChartSeries, colorByPoint: true },
    ];

    const chartSettings: Highcharts.Options = {
      legend: { enabled: false },
      chart: {
        height: 340,
        events: {
          render() {
            const proposedPlanSeries = this.series[0];

            const principalRetirementIndex = proposedPlanSeries.points.find(
              ({ category }) => +category === principalRetirementYear,
            );

            const principalLifeExpectancyIndex = proposedPlanSeries.points.find(
              ({ category }) => +category === principalLifeExpectancy,
            );

            if (principalRetirementIndex) {
              renderDot({
                chart: this,
                point: principalRetirementIndex,
                id: 'principal-retirement-dot',
                color: SERIES_COLORS[2],
              });
            }

            if (principalLifeExpectancyIndex) {
              renderVerticalLineWithSquare({
                chart: this,
                point: principalLifeExpectancyIndex,
                id: 'principal-life-expectency-dot',
                color: SERIES_COLORS[3],
              });
            }

            if (spouseName) {
              const spouseRetirementIndex = proposedPlanSeries.points.find(
                ({ category }) => +category === spouseRetirementYear,
              );

              const spouseLifeExpectancyIndex = proposedPlanSeries.points.find(
                ({ category }) => +category === spouseLifeExpectancy,
              );

              if (spouseRetirementIndex) {
                renderDot({
                  chart: this,
                  point: spouseRetirementIndex,
                  id: 'spouse-retirement-dot',
                  color: SERIES_COLORS[0],
                });
              }

              if (spouseLifeExpectancyIndex) {
                renderVerticalLineWithSquare({
                  chart: this,
                  point: spouseLifeExpectancyIndex,
                  id: 'life-expectancy-retirement-dot',
                  color: SERIES_COLORS[1],
                  offset: principalLifeExpectancyIndex === spouseLifeExpectancyIndex ? 11 : 0,
                });
              }
            }
          },
        },
      },
      yAxis: {
        max: 1,
        labels: {
          // eslint-disable-next-line object-shorthand
          formatter: function() {
            // eslint-disable-next-line react/no-this-in-sfc
            return formatPercentage(+this.value);
          },
        },
      },
      xAxis: {},
      plotOptions: {
        series: {
          dataLabels: {
            enabled: false,
          },
        },
      },

      tooltip: {
        shared: true,

        formatter() {
          let tooltip = ``;

          if ((this as any).points) {
            (this as any).points.forEach(({ color, point }) => {
              tooltip = `
                <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px; width: 320px;" >
                    ${renderTooltip({
                      label: 'Probability of Success:',
                      value: formatPercentage(point.y, {}),
                    })}

                    ${renderTooltip({
                      label: 'Year:',
                      value: (this as any).x,
                      color,
                    })}
            
                    ${
                      point.principalAge !== null
                        ? renderTooltip({
                            label: principalName,
                            value: point.principalAge,
                            color: SERIES_COLORS[2],
                          })
                        : ''
                    }

                    ${
                      point.spouseAge !== null && spouseName
                        ? renderTooltip({
                            label: spouseName,
                            value: point.spouseAge,
                            color: SERIES_COLORS[0],
                          })
                        : ''
                    }
                </div>
                `;
            });
          }

          return `${tooltip}`;
        },
      },
    };

    return {
      series,
      categories,
      tableData: ChartData,
      principalName,
      principalRetirementYear,
      spouseRetirementYear,
      spouseName,
      principalGender,
      spouseGender,
      monteCarloHighRange,
      monteCarloMediumRange,
      monteCarloLowRange,
      chartSettings,
      principalLifeExpectancy,
      spouseLifeExpectancy,
    };
  }, [queryInfo.data]);

  return {
    ...queryInfo,
    data,
  };
};
